import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ShieldCheck, ShieldAlert, Loader2, RefreshCw, ExternalLink,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mra_jwt";
const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((cfg) => {
  const t = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  if (t) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${t}`;
  }
  return cfg;
});

const fmtChecked = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
};

/**
 * DomainHealthWidget — one-tap "is the public site up?" gauge.
 *
 * Pings the domain configured in the backend's FRONTEND_ORIGIN env var.
 * Auto-runs once on mount; admins can hit Re-check on demand.
 *
 * States:
 *   · loading  — grey pill with spinner
 *   · ok       — grass tick + latency + last-checked time
 *   · error    — scarlet warning + short error type + last-checked time
 */
export const DomainHealthWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const run = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await client.get("/admin/health/live-domain");
      setData(r.data);
    } catch (e) {
      setErr(
        e?.response?.data?.detail ||
          "Couldn't run the probe. The backend may be waking up."
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    run();
  }, [run]);

  const ok = data?.ok === true;
  const url = data?.url || "your live domain";

  // Visual tone: grass on ok, scarlet on failure, olive/loading otherwise.
  const toneCls = loading
    ? "border-border bg-bone text-olive"
    : ok
    ? "border-grass/25 bg-grass/[0.07] text-grass"
    : "border-scarlet/25 bg-scarlet/[0.06] text-scarlet";

  const Icon = loading ? Loader2 : ok ? ShieldCheck : ShieldAlert;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="domain-health-widget"
      className={`flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border px-4 py-3 text-sm ${toneCls}`}
    >
      <span className="inline-flex items-center gap-2 font-semibold">
        <Icon
          className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
          strokeWidth={2.2}
        />
        <span data-testid="domain-health-label">
          {loading ? "Checking public site…" : ok ? "Public site is up" : "Public site is not responding"}
        </span>
      </span>

      {!loading && data && (
        <span className="text-xs text-ink/70">
          <a
            href={data.url || "#"}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="domain-health-url"
            className="inline-flex items-center gap-1 font-medium text-ink hover:underline"
          >
            {(data.url || "").replace(/^https?:\/\//, "")}
            <ExternalLink className="h-3 w-3" strokeWidth={2} />
          </a>
          {ok && data.status ? (
            <>
              {" · "}
              <span data-testid="domain-health-detail">
                HTTP {data.status} · {typeof data.latency_ms === "number" ? `${data.latency_ms}ms` : "—"}
                {data.ssl_valid ? " · SSL valid" : ""}
              </span>
            </>
          ) : null}
          {!ok && data.error ? (
            <>
              {" · "}
              <span className="italic" data-testid="domain-health-detail">
                {data.error}
              </span>
            </>
          ) : null}
          {data.checked_at && (
            <>
              {" · "}
              <span className="text-ink/50">last checked {fmtChecked(data.checked_at)}</span>
            </>
          )}
        </span>
      )}

      {err && (
        <span className="text-xs italic text-scarlet" data-testid="domain-health-fetch-error">
          {err}
        </span>
      )}

      <button
        type="button"
        onClick={run}
        disabled={loading}
        data-testid="domain-health-recheck"
        className="ml-auto inline-flex items-center gap-1 rounded-full border border-ink/15 bg-sand px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-ink transition-colors hover:border-ink disabled:opacity-50"
      >
        <RefreshCw
          className={`h-3 w-3 ${loading ? "animate-spin" : ""}`}
          strokeWidth={2.2}
        />
        Re-check
      </button>
    </motion.div>
  );
};
